import {Inject, Injectable} from '@angular/core';
import {GoogleTagService} from '@core/services/google-tag.service';
import {GoogleAdsService} from '@core/services/google-ads.service';
import {environment} from '@environments/environment';
import {ConsentService} from '@core/services/consent.service';
import {DOCUMENT} from '@angular/common';
import {Store} from '@ngxs/store';

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  readonly knownCookieNamesToManage = [
    {
      name: '_ga',
      type: 'String',
    },
    {name: /^_ga_/, type: 'RegExp'},
    {name: '_bame', type: 'String'},
    {name: '_baml', type: 'String'},
    {name: '_bamcdc', type: 'String'},
  ];

  get isGdprClient(): boolean {
    return this.gdprClients.includes(environment.name);
  }

  private readonly gdprClients = ['ekstraklasa', 'legia'];

  constructor(
    private readonly googleTagService: GoogleTagService,
    private readonly googleAdsService: GoogleAdsService,
    private readonly consentService: ConsentService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  private initializeGtagBase(): void {
    this.googleTagService.declareGtag();
    this.googleTagService.setDefaultConsentGtag();
  }

  initGoogleTagWithCmp(gtagId: string) {
    this.initializeGtagBase();
    this.googleTagService.addGtagScript(gtagId);
  }

  initGoogleTagWithoutCmp(gtagId: string) {
    const consent = this.consentService.getConsentCookie();
    this.initializeGtagBase();
    if (consent) {
      this.googleTagService.updateConsentGtag(consent);
    }

    this.googleTagService.addGtagScript(gtagId);
  }

  initGoogleAds() {
    this.googleAdsService.addGoogleAdsScript();

    if (this.isGdprClient) {
      const consent = this.consentService.getConsentCookie();

      this.googleAdsService.pauseAds();

      if (consent) {
        const marketingConsent =
          this.consentService.getGoogleMarketingPersonalizationConsent();

        if (marketingConsent) {
          this.googleAdsService.startAds();
        } else {
          this.googleAdsService.requestNonPersonalizedAds();
        }
      }
    }
  }

  removeCookiesBeforeConsent(): void {
    if (this.isGdprClient && !this.consentService.getConsentCookie()) {
      this.removeUnWantedCookies();
    }
  }

  removeUnWantedCookies(): void {
    const collectedCookiesNames = this.document.cookie
      .split(';')
      .map(cookie => cookie.split('=')[0].replace(/(^\s*)|(\s*&)/, ''));

    this.knownCookieNamesToManage.forEach(cookieName => {
      if (cookieName.type === 'String') {
        if (collectedCookiesNames.indexOf(cookieName.name as string) !== -1) {
          this.removeCookie(cookieName.name as string);
        }
      }

      if (cookieName.type === 'RegExp') {
        const regExp = cookieName.name as RegExp;

        collectedCookiesNames.forEach(collectedCookieName => {
          if (regExp.test(collectedCookieName)) {
            this.removeCookie(collectedCookieName);
          }
        });
      }
    });
  }

  removeCookie(name: string): void {
    this.document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
  }
}
